<template>
  <div>
    <div :class="containerClass" class="d-flex justify-content-between mt-3">
      <h2>
        View Quizzes
        <i class="material-icons icon-16pt clickable-item" v-b-popover.hover.top="`View all quizzes.`">info</i>
      </h2>
      <div>
        <b-btn size="md" variant="primary" :to="{ name: 'create-quiz' }">
          Add Quiz
        </b-btn>
      </div>
    </div>

    <div class="page-section">
      <div :class="containerClass">
        <div class="card mb-0">
          <div class="card-header">
            <div class="row align-items-center" style="white-space: nowrap">
              <div class="col-lg-auto">
                <form class="search-form search-form--light d-lg-inline-flex mb-2" @submit.prevent="">
                  <b-form-input class="w-lg-auto" placeholder="Search Quizzes" v-model="searchTerm" @input="onSearch" />
                  <b-btn variant="flush" type="submit">
                    <md-icon>search</md-icon>
                  </b-btn>
                </form>
              </div>
            </div>

            <b-table
              :fields="tableColumns"
              :items="quizzes"
              v-if="quizzes.length"
              :busy="isLoading"
              head-variant="light"
              class="table-nowrap"
              hover
              responsive
              no-local-sorting
              @sort-changed="onSortChange"
            >
              <template #table-busy>
                <div class="text-center my-2">
                  <b-spinner class="align-middle mr-2"></b-spinner>
                  <strong>Loading...</strong>
                </div>
              </template>

              <template #cell(title)="data">
                <strong>{{ data.item.title }}</strong>
              </template>

              <template #cell(max_attempts)="data">
                {{ data.item.max_attempts || 'Unlimited' }}
              </template>

              <template #cell(duration_minutes)="data">
                {{ data.item.duration_minutes ? `${data.item.duration_minutes} min` : 'No limit' }}
              </template>

              <template #cell(total_marks)="data">
                {{ data.item.total_marks }}
              </template>

              <template #cell(passing_percentage)="data"> {{ data.item.passing_percentage }}% </template>

              <template #cell(actions)="data">
                <router-link
                  :to="{
                    name: 'edit-quiz',
                    params: { id: data.item.id },
                  }"
                  class="svg-icon mr-2"
                  v-b-popover.hover.left
                  :title="$t('generalMsgs.edit')"
                >
                  <md-icon class="text-info">edit</md-icon>
                </router-link>

                <a
                  href="#"
                  @click.prevent="confirmDeleteQuiz(data.item.id)"
                  v-b-popover.hover.left
                  :title="$t('generalMsgs.remove')"
                >
                  <md-icon class="text-danger">delete</md-icon>
                </a>
              </template>
            </b-table>
            <div class="card-footer">
              <pagination
                v-model="currentPage"
                :total-rows="totalQuizzes"
                :per-page="perPage"
                @change="onPageChange"
                aria-controls="quizzes-table"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce, get } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import Page from '@/components/Page.vue';
import { DEFAULT_PAGE_SIZE } from '../../../common/constants';
// import { formatFullDate } from '@/common/utils';
import Pagination from '../../../components/Ui/Pagination.vue';

export default {
  components: {
    Pagination,
  },
  extends: Page,

  data() {
    return {
      title: 'View Quizzes',
      isLoading: true,
      perPage: DEFAULT_PAGE_SIZE,
      currentPage: 1,
      totalQuizzes: 0,
      searchTerm: null,
      quizzes: [],
      limit: 15,
      offset: 0,
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    tableColumns() {
      return [
        { key: 'title', label: 'Title', sortable: true, tdClass: 'clickable-item', thStyle: { minWidth: '200px' } },
        { key: 'max_attempts', label: 'Max Attempts', class: 'text-center' },
        { key: 'duration_minutes', label: 'Duration', class: 'text-center' },
        { key: 'total_marks', label: 'Total Marks', class: 'text-center' },
        { key: 'passing_percentage', label: 'Passing %', class: 'text-center' },
        // {
        //   key: 'created_at',
        //   label: 'Created At',
        //   formatter: value => (value ? formatFullDate(value) : value),
        //   sortable: true,
        // },
        // {
        //   key: 'updated_at',
        //   label: 'Updated At',
        //   formatter: value => (value ? formatFullDate(value) : value),
        //   sortable: true,
        // },
        {
          key: 'actions',
          tdClass: 'text-right',
          thStyle: { minWidth: '100px' },
        },
      ];
    },
  },

  methods: {
    get,
    ...mapActions('quizzes', ['getQuizzes', 'deleteQuiz']),

    onPageChange(pageNum) {
      this.fetchAllQuizzes(pageNum);
    },

    async confirmDeleteQuiz(id) {
      try {
        const isConfirmed = await this.$bvModal.msgBoxConfirm('Are you sure you want to delete this quiz?', {
          title: 'Delete Quiz',
          size: 'md',
          buttonSize: 'sm',
          okVariant: 'danger',
          cancelVariant: 'secondary',
          okTitle: 'Yes',
          cancelTitle: 'No',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true,
        });
        if (isConfirmed) {
          await this.onDeleteQuiz(id);
          this.fetchAllQuizzes();
        }
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },

    async onDeleteQuiz(id) {
      try {
        await this.deleteQuiz(id);
        this.makeToast({ variant: 'success', msg: 'Quiz successfully deleted' });
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },

    async fetchAllQuizzes(params = {}) {
      this.isLoading = true;
      try {
        const res = await this.getQuizzes({
          ...(this.currentPage ? { page: this.currentPage } : {}),
          ...(this.searchTerm && { search: this.searchTerm }),
          ...params,
        });
        this.quizzes = res.data || [];
        this.totalQuizzes = res.count || 0;
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLoading = false;
    },

    onSortChange(context) {
      const ordering = context.sortDesc ? `-${context.sortBy}` : context.sortBy;
      this.fetchAllQuizzes({ ordering });
    },

    onSearch() {
      this.debouncedQuizSearch(this);
    },

    debouncedQuizSearch: debounce(vm => {
      vm.fetchAllQuizzes();
    }, 500),
  },

  async mounted() {
    await this.fetchAllQuizzes();
  },
};
</script>

<style scoped lang="scss">
.loader {
  text-align: center;
  color: #bbbbbb;
  margin-inline: auto;
}
</style>
